import { useCallback, useState } from 'react';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import { Box, Form, FormLayout, TextField } from '@shopify/polaris';
import { ICardMediaAsset } from '@/types/media';
import useUpdateMedia from '@/hooks/useUpdateMedia';
import { Avatar } from '@/types/avatar';
import { PersonalAvatarViewData } from '@/api/personal-avatars';
import BasicBanner from '@/components/BasicBanner';

type RenameMediaAssetModalProps = {
  mediaAsset: ICardMediaAsset | Avatar | PersonalAvatarViewData;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const RenameMediaAssetModal = ({ mediaAsset, isOpen, setIsOpen }: RenameMediaAssetModalProps) => {
  const [newName, setName] = useState(mediaAsset.name || '');

  const [error, setError] = useState('');
  const { renameMutation } = useUpdateMedia(mediaAsset, {
    onSuccess: () => setIsOpen(false),
    onError: (asset) => setError(`Failed to rename ${asset}`),
  });
  const { mutate } = renameMutation;

  const handleSave = useCallback(
    (event: React.FormEvent<HTMLFormElement | HTMLButtonElement>) => {
      event.preventDefault();
      if (!newName || newName === mediaAsset.name) return;
      mutate(newName);
    },
    [newName, mediaAsset.name]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <Modal open={isOpen} onHide={handleClose}>
      <TitleBar title="Rename Creation">
        <button
          variant="primary"
          onClick={handleSave}
          disabled={newName.length === 0 || renameMutation.isPending || newName === mediaAsset.name}
        >
          Save
        </button>
        <button onClick={handleClose} disabled={renameMutation.isPending}>
          Cancel
        </button>
      </TitleBar>
      <Box padding="400">
        <BasicBanner
          tone="critical"
          title="Error"
          hidden={!error}
          boxComponentProps={{ paddingBlockEnd: '200' }}
          onDismiss={() => setError('')}
        >
          {error}
        </BasicBanner>
        <Form onSubmit={handleSave}>
          <FormLayout>
            <TextField
              loading={renameMutation.isPending}
              disabled={renameMutation.isPending}
              label="Name:"
              value={newName}
              onChange={setName}
              autoComplete="off"
              requiredIndicator
              minLength={1}
            />
          </FormLayout>
        </Form>
      </Box>
    </Modal>
  );
};
